/**
 * Firestore-backed NAR ingestion log types and pagination hook for the NAR configuration screen.
 */

import {
  limit,
  orderBy,
  QueryDocumentSnapshot,
  where,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FirestoreService } from "../../../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../../../shared/api/firebase/references";

export interface NarIngestionLog {
  id: string;
  created_at_time: Date;
  document_type: string;
  month: number;
  output_file_name: string;
  payer_type: string;
  processing_time_seconds: number;
  records_inserted: number;
  records_processed: number;
  source_file?: string;
  status: string;
  sub_type: string;
  year: number;
  processed_csv_path?: string;
  is_rejected?: boolean;
  effective_date?: string;
  never_expire?: boolean;
}

const DEFAULT_PAGE_SIZE = 10;

/** Max documents pulled per Firestore branch when searching (merged client-side). */
const SEARCH_FETCH_LIMIT = 80;

const NAR_LOGS_PATH = FirestoreCollectionReference.narIngestionLogs();

/**
 * Result of a merged insurance/plan prefix search over parallel Firestore branches.
 */
export interface NarInsuranceSearchFetchResult {
  logs: NarIngestionLog[];
  allBranchesFailed: boolean;
}

/**
 * Returns a numeric sort key for `created_at_time`, or `0` when the value is missing,
 * not a `Date`, or an invalid date — so sorting never throws and bad rows sort last.
 *
 * @param log - Ingestion log row
 * @returns Epoch milliseconds, or `0` if unusable
 */
function narLogCreatedAtSortKey(log: NarIngestionLog): number {
  const d = log.created_at_time;
  if (!(d instanceof Date)) {
    return 0;
  }
  const t = d.getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Deduplicates ingestion rows by id and sorts newest first.
 *
 * Rows without a usable `created_at_time` sort last so a single bad document cannot
 * reject the whole merge.
 *
 * @param batches - Result sets from parallel prefix queries
 * @returns Merged, de-duplicated logs sorted by `created_at_time` descending
 */
function mergeAndSortNarLogs(batches: NarIngestionLog[][]): NarIngestionLog[] {
  const byId = new Map<string, NarIngestionLog>();
  for (const batch of batches) {
    for (const row of batch) {
      byId.set(row.id, row);
    }
  }
  return [...byId.values()].sort(
    (a, b) => narLogCreatedAtSortKey(b) - narLogCreatedAtSortKey(a),
  );
}

/**
 * Fetches NAR ingestion logs whose insurance (`payer_type`) or plan (`sub_type`) matches
 * prefix-style Firestore range filters, using parallel queries merged in memory.
 *
 * @param rawTerm - User search string (trimmed inside this function)
 * @returns Merged matching logs (newest first) and whether every Firestore branch failed
 */
async function fetchNarLogsByInsuranceSearch(
  rawTerm: string,
): Promise<NarInsuranceSearchFetchResult> {
  const trimmed = rawTerm.trim();
  if (!trimmed) {
    return { logs: [], allBranchesFailed: false };
  }

  // `payer_type` and `sub_type` are stored lower_snake_case; normalize user input the same way
  // so multi-word terms (e.g. "gold plan") prefix-match `gold_plan` in Firestore.
  const searchPrefix = trimmed.toLowerCase().replace(/\s+/g, "_");
  const searchEnd = `${searchPrefix}\uf8ff`;

  // Range filters on payer_type/sub_type may only use orderBy on that same field (Firestore).
  // Final sort by created_at_time is done in mergeAndSortNarLogs. A second orderBy here
  // required composite indexes and caused Promise.all to reject, showing empty search results.
  const tasks: Promise<NarIngestionLog[]>[] = [
    FirestoreService.getDocumentsWithConstraints<NarIngestionLog>(
      NAR_LOGS_PATH,
      [
        where("payer_type", ">=", searchPrefix),
        where("payer_type", "<=", searchEnd),
        orderBy("payer_type"),
        limit(SEARCH_FETCH_LIMIT),
      ],
    ),
    FirestoreService.getDocumentsWithConstraints<NarIngestionLog>(
      NAR_LOGS_PATH,
      [
        where("sub_type", ">=", searchPrefix),
        where("sub_type", "<=", searchEnd),
        orderBy("sub_type"),
        limit(SEARCH_FETCH_LIMIT),
      ],
    ),
  ];

  const settled = await Promise.allSettled(tasks);
  const batches: NarIngestionLog[][] = [];
  let fulfilledCount = 0;
  settled.forEach((result, index) => {
    if (result.status === "fulfilled") {
      fulfilledCount += 1;
      batches.push(result.value);
    } else {
      console.error(
        `NAR ingestion search branch ${index} failed:`,
        result.reason,
      );
    }
  });
  const allBranchesFailed = fulfilledCount === 0;
  return {
    logs: mergeAndSortNarLogs(batches),
    allBranchesFailed,
  };
}

/**
 * Subscribes to NAR ingestion logs for the current page (newest first) with cursor-based pagination.
 * Non-search mode requests `pageSize + 1` from Firestore and trims to `pageSize` so `hasNextPage` is
 * accurate when the collection size is an exact multiple of `pageSize` (avoids an empty trailing page).
 * When `searchInput` is non-empty (after debounce), loads merged prefix matches on insurance and plan
 * fields once per debounced term and paginates those results client-side (page changes do not refetch).
 *
 * @param pageSize - Maximum documents per page (default 10)
 * @param searchInput - Raw search text for insurance / plan prefix match
 * @returns Current page rows, loading state, search failure flag when all branches error, pagination controls
 */
export const useFetchGridInfo = (
  pageSize: number = DEFAULT_PAGE_SIZE,
  searchInput: string = "",
) => {
  const [subscriptionData, setSubscriptionData] = useState<NarIngestionLog[]>(
    [],
  );
  const [searchMergedResults, setSearchMergedResults] = useState<
    NarIngestionLog[]
  >([]);
  /** When true, debounced search finished but every Firestore branch rejected (distinct from no matches). */
  const [searchAllBranchesFailed, setSearchAllBranchesFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNextPageServer, setHasNextPageServer] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const endCursorsRef = useRef<QueryDocumentSnapshot[]>([]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!debouncedSearch) {
      setSearchAllBranchesFailed(false);
    }
  }, [debouncedSearch]);

  /**
   * Loads merged prefix search results when the debounced term changes only.
   * Does not depend on `pageIndex` / `pageSize` so client-side search pagination avoids refetch and loading flicker.
   */
  useEffect(() => {
    if (!debouncedSearch) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSearchAllBranchesFailed(false);
    fetchNarLogsByInsuranceSearch(debouncedSearch)
      .then(({ logs, allBranchesFailed }) => {
        if (!cancelled) {
          setSearchMergedResults(logs);
          setSearchAllBranchesFailed(allBranchesFailed);
          setLoading(false);
        }
      })
      .catch((error: unknown) => {
        console.error("Error searching NAR ingestion logs:", error);
        if (!cancelled) {
          setSearchMergedResults([]);
          setSearchAllBranchesFailed(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  /**
   * Realtime Firestore pagination when not searching; re-subscribes on `pageIndex` / `pageSize` changes.
   * When `debouncedSearch` is set, skips subscription (search results drive the grid via `searchMergedResults`).
   *
   * If `pageIndex > 0` but the cursor for the previous page is not yet stored (e.g. rapid Next before the
   * prior snapshot ran), we skip subscribing: without `startAfter` Firestore would return page 1 and the
   * snapshot callback would write that cursor into `endCursorsRef.current[pageIndex]`, corrupting pagination.
   */
  useEffect(() => {
    if (debouncedSearch) {
      return;
    }

    if (pageIndex > 0 && !endCursorsRef.current[pageIndex - 1]) {
      setLoading(false);
      return;
    }

    setSearchMergedResults([]);
    setLoading(true);

    const lastDocForPage: QueryDocumentSnapshot | undefined =
      pageIndex === 0 ? undefined : endCursorsRef.current[pageIndex - 1];

    const unsubscribe = FirestoreService.paginateQueryRealtime<NarIngestionLog>(
      NAR_LOGS_PATH,
      pageSize,
      ({ docs, lastVisible, hasNextPage }) => {
        setSubscriptionData(docs);
        if (lastVisible) {
          endCursorsRef.current[pageIndex] = lastVisible;
        }
        setHasNextPageServer(
          hasNextPage !== undefined ? hasNextPage : docs.length === pageSize,
        );
        setLoading(false);
      },
      (error) => {
        console.error("Error listening to NAR ingestion logs:", error);
        setLoading(false);
      },
      lastDocForPage,
      [orderBy("created_at_time", "desc")],
      { overFetchOneForHasNext: true },
    );

    return () => {
      unsubscribe();
    };
  }, [debouncedSearch, pageIndex, pageSize]);

  const goToNextPage = useCallback(() => {
    setPageIndex((previous) => previous + 1);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setPageIndex((previous) => Math.max(0, previous - 1));
  }, []);

  const hasPreviousPage = pageIndex > 0;

  const data = useMemo(() => {
    if (debouncedSearch) {
      return searchMergedResults.slice(
        pageIndex * pageSize,
        (pageIndex + 1) * pageSize,
      );
    }
    return subscriptionData;
  }, [
    debouncedSearch,
    pageIndex,
    pageSize,
    searchMergedResults,
    subscriptionData,
  ]);

  const hasNextPage = debouncedSearch
    ? (pageIndex + 1) * pageSize < searchMergedResults.length
    : hasNextPageServer;

  return {
    data,
    loading,
    searchAllBranchesFailed,
    pageIndex,
    pageNumber: pageIndex + 1,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
  };
};
